import React, { useState } from 'react';
import { Card, Form, Input, Select, Button, Radio, Switch, Typography, Row, Col, Table, Tag } from 'antd';
import { SettingOutlined, PlusOutlined } from '@ant-design/icons';

const { Title } = Typography;
const { Option } = Select;

const PredictionOutputConfig: React.FC = () => {
    const [outputType, setOutputType] = useState('csv');
    const [form] = Form.useForm();

    const existingConfigs = [
        { id: '1', name: 'CSV导出配置', type: 'CSV', destination: '/exports/daily/', frequency: '每日', status: '启用' },
        { id: '2', name: '数据库配置', type: 'Database', destination: 'MySQL-主库', frequency: '实时', status: '启用' },
        { id: '3', name: 'API接口配置', type: 'API', destination: 'http://api.example.com', frequency: '每小时', status: '禁用' },
    ];

    const columns = [
        {
            title: '配置名称',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: '类型',
            dataIndex: 'type',
            key: 'type',
            render: (type: string) => {
                const colorMap = {
                    'CSV': 'blue',
                    'Database': 'green',
                    'API': 'orange'
                };
                return <Tag color={colorMap[type as keyof typeof colorMap]}>{type}</Tag>;
            },
        },
        {
            title: '目标位置',
            dataIndex: 'destination',
            key: 'destination',
        },
        {
            title: '频率',
            dataIndex: 'frequency',
            key: 'frequency',
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <Tag color={status === '启用' ? 'green' : 'red'}>{status}</Tag>
            ),
        },
        {
            title: '操作',
            key: 'action',
            render: () => (
                <div>
                    <Button type="link" size="small">编辑</Button>
                    <Button type="link" size="small">测试</Button>
                    <Button type="link" size="small" danger>删除</Button>
                </div>
            ),
        },
    ];

    return (
        <div className="page-transition">
            <Title level={2}>
                <SettingOutlined style={{ marginRight: '8px' }} />
                预测输出配置
            </Title>

            <Row gutter={[16, 16]}>
                <Col span={12}>
                    <Card title="新增输出配置">
                        <Form
                            form={form}
                            layout="vertical"
                            initialValues={{ outputType: 'csv', enabled: true }}
                        >
                            <Form.Item label="输出类型" name="outputType">
                                <Radio.Group value={outputType} onChange={(e) => setOutputType(e.target.value)}>
                                    <Radio value="csv">CSV文件</Radio>
                                    <Radio value="database">数据库</Radio>
                                    <Radio value="api">API接口</Radio>
                                </Radio.Group>
                            </Form.Item>

                            <Form.Item label="配置名称" name="configName">
                                <Input placeholder="请输入配置名称" />
                            </Form.Item>

                            {outputType === 'csv' && (
                                <>
                                    <Form.Item label="文件路径" name="filePath">
                                        <Input placeholder="例如: /exports/predictions/" />
                                    </Form.Item>
                                    <Form.Item label="文件格式" name="fileFormat">
                                        <Select placeholder="选择文件格式">
                                            <Option value="csv">CSV</Option>
                                            <Option value="xlsx">Excel</Option>
                                            <Option value="json">JSON</Option>
                                        </Select>
                                    </Form.Item>
                                </>
                            )}

                            {outputType === 'database' && (
                                <>
                                    <Form.Item label="数据库类型" name="dbType">
                                        <Select placeholder="选择数据库类型">
                                            <Option value="mysql">MySQL</Option>
                                            <Option value="postgresql">PostgreSQL</Option>
                                            <Option value="oracle">Oracle</Option>
                                        </Select>
                                    </Form.Item>
                                    <Form.Item label="连接字符串" name="connectionString">
                                        <Input placeholder="数据库连接字符串" />
                                    </Form.Item>
                                    <Form.Item label="表名" name="tableName">
                                        <Input placeholder="目标表名" />
                                    </Form.Item>
                                </>
                            )}

                            {outputType === 'api' && (
                                <>
                                    <Form.Item label="API地址" name="apiUrl">
                                        <Input placeholder="http://api.example.com/predictions" />
                                    </Form.Item>
                                    <Form.Item label="请求方法" name="httpMethod">
                                        <Select placeholder="选择HTTP方法">
                                            <Option value="POST">POST</Option>
                                            <Option value="PUT">PUT</Option>
                                            <Option value="PATCH">PATCH</Option>
                                        </Select>
                                    </Form.Item>
                                    <Form.Item label="认证Token" name="authToken">
                                        <Input.Password placeholder="API认证Token" />
                                    </Form.Item>
                                </>
                            )}

                            <Form.Item label="输出频率" name="frequency">
                                <Select placeholder="选择输出频率">
                                    <Option value="realtime">实时</Option>
                                    <Option value="hourly">每小时</Option>
                                    <Option value="daily">每日</Option>
                                    <Option value="weekly">每周</Option>
                                </Select>
                            </Form.Item>

                            <Form.Item label="启用配置" name="enabled" valuePropName="checked">
                                <Switch />
                            </Form.Item>

                            <Form.Item>
                                <Button type="primary" htmlType="submit" block>
                                    保存配置
                                </Button>
                            </Form.Item>
                        </Form>
                    </Card>
                </Col>

                <Col span={12}>
                    <Card title="配置说明">
                        <div style={{ padding: '16px' }}>
                            <h4>CSV文件输出</h4>
                            <p>将预测结果导出为CSV或Excel文件，适合离线分析和报告生成。</p>

                            <h4>数据库输出</h4>
                            <p>直接将预测结果写入数据库表，便于其他系统集成和实时查询。</p>

                            <h4>API接口输出</h4>
                            <p>通过HTTP API将预测结果推送到第三方系统，支持实时数据流转。</p>
                        </div>
                    </Card>
                </Col>
            </Row>

            <Card
                title="已配置的输出方式"
                extra={<Button type="primary" icon={<PlusOutlined />}>新增配置</Button>}
                style={{ marginTop: '16px' }}
            >
                <Table
                    columns={columns}
                    dataSource={existingConfigs}
                    rowKey="id"
                    pagination={false}
                />
            </Card>
        </div>
    );
};

export default PredictionOutputConfig; 